import { TooltipInfo } from '@sola/ark-core/molecules/tooltip-info/tooltip-info.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';

import type {
  AvatarCommitment,
  CommitmentAvatarProps,
} from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';

interface CommitmentAvatarGroupProps {
  commitments: AvatarCommitment[];
  layout?: 'spaced' | 'stacked';
  itemsToShow?: number;
  purpose?: CommitmentAvatarProps['purpose'];
  size?: CommitmentAvatarProps['size'];
  dynamicSize?: boolean;
  tooltipMessage?: string;
  usePortal?: boolean;
}

const getDynamicSize = (count: number) => {
  return count > 1 ? 'sm' : 'lg';
};

export const CommitmentAvatarGroup = ({
  commitments,
  layout = 'stacked',
  itemsToShow = 3,
  purpose,
  size,
  dynamicSize,
  tooltipMessage,
  usePortal = true,
}: CommitmentAvatarGroupProps) => {
  const items = commitments.slice(0, itemsToShow);
  return (
    <div
      className={cn({
        'flex flex-row gap-comfy-xs': layout === 'spaced',
        'flex items-center gap-snug-md': layout === 'stacked',
      })}
    >
      <div
        className={cn('flex flex-row', {
          'gap-snug-xl': layout === 'spaced',
        })}
      >
        {items.map((commitment, index) => {
          const shouldShowRemaining =
            index === itemsToShow - 1 && commitments.length > itemsToShow;
          /** NOTE: plus one to account for this item plus remaining */
          const remainingCount = commitments.length - itemsToShow + 1;
          const itemSize = dynamicSize ? getDynamicSize(items.length) : size;

          return (
            <CommitmentAvatar
              key={commitment.internalId}
              {...(shouldShowRemaining
                ? { text: `+${remainingCount}` }
                : { commitment })}
              size={itemSize}
              isStacked={index < items.length - 1 && layout === 'stacked'}
              purpose={purpose}
            />
          );
        })}
      </div>
      {tooltipMessage && (
        <TooltipInfo
          contentClassName="label-md lg:label-lg"
          align="center"
          side="top"
          alignOffset={-12}
          collisionPadding={16}
          usePortal={usePortal}
        >
          {tooltipMessage}
        </TooltipInfo>
      )}
    </div>
  );
};
